import { Component, OnChanges, OnInit, Output, EventEmitter, Input, SimpleChanges } from '@angular/core';
import { AlertService } from 'src/app/shared/services/alert.service';
import { msg } from 'src/app/constants/msg';
import { RegisterCompanyResponse } from '@ent-regis/entregis-ts-angular';

@Component({
  selector: 'app-company-presentation-section',
  templateUrl: './company-presentation-section.component.html',
  styleUrls: ['./company-presentation-section.component.scss']
})
export class CompanyPresentationSectionComponent implements OnInit, OnChanges {
  
  @Input() registerCompanyInfo: RegisterCompanyResponse;
  @Input() selectedFile: File = null;
  @Output() selectedFileChange = new EventEmitter<File>();
  @Output() submitPresentation = new EventEmitter<File>();

  existingPresentationUrl: string = null;
  existingPresentationFileName: string = null;
  removeExistingPresentation = false;

  private readonly maxFileSize = 20 * 1024 * 1024;
  private readonly allowedExtensions = ['pptx', 'pdf', 'jpg', 'png'];

  constructor(
    private alert: AlertService,
  ) { }

  async ngOnInit() {
    this.syncExistingPresentationFromInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.registerCompanyInfo && !this.selectedFile) {
      this.syncExistingPresentationFromInput();
    }
  }

  private syncExistingPresentationFromInput(): void {
    this.existingPresentationUrl = this.registerCompanyInfo && this.registerCompanyInfo.presentationUrl
      ? this.registerCompanyInfo.presentationUrl
      : null;
    this.existingPresentationFileName = this.registerCompanyInfo && this.registerCompanyInfo.originalPresentationFileName
      ? this.registerCompanyInfo.originalPresentationFileName
      : null;
  }

  get canUploadFile(): boolean {
    return !this.selectedFile && !this.existingPresentationFileName;
  }

  get canSavePresentation(): boolean {
    return !!this.selectedFile || this.removeExistingPresentation;
  }

  get displayFileName(): string {
    if (this.selectedFile) {
      return this.selectedFile.name;
    }
    return this.existingPresentationFileName;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input && input.files && input.files.length > 0 ? input.files[0] : null;

    if (!file) {
      return;
    }

    const extension = this.getFileExtension(file.name);

    if (!this.allowedExtensions.includes(extension)) {
      const uploadError = 'Unsupported file format. Please upload .pptx, .pdf, .jpg, or .png.';
      this.alert.showError(uploadError);
      input.value = '';
      return;
    }

    if (file.size > this.maxFileSize) {
      this.alert.showError(msg.error.uploadFileLarge);
      input.value = '';
      return;
    }

    this.selectedFile = file;
    this.selectedFileChange.emit(file);
    this.existingPresentationFileName = null;
    this.existingPresentationUrl = null;
    input.value = '';
  }

  removeFile(): void {
    this.selectedFileChange.emit(null);
    this.existingPresentationUrl = null;
    this.existingPresentationFileName = null;
    this.removeExistingPresentation = true;
  }

  downloadFile(): void {
    if (this.selectedFile) {
      const url = URL.createObjectURL(this.selectedFile);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.selectedFile.name;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    if (this.existingPresentationUrl) {
      const a = document.createElement('a');
      a.href = this.existingPresentationUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      if (this.existingPresentationFileName) {
        a.download = this.existingPresentationFileName;
      }
      a.click();
    }
  }

  getFileIconClass(fileName: string): string {
    const extension = this.getFileExtension(fileName);
    if (extension === 'pptx') {
      return 'fas fa-file-powerpoint';
    }
    if (extension === 'pdf') {
      return 'fas fa-file-pdf';
    }
    if (extension === 'jpg' || extension === 'png') {
      return 'fas fa-file-image';
    }
    return 'fas fa-file';
  }

  private getFileExtension(fileName: string): string {
    const index = fileName.lastIndexOf('.');
    if (index < 0) {
      return '';
    }
    return fileName.substring(index + 1).toLowerCase();
  }

  onSubmit(): void {
    if (this.removeExistingPresentation) {
      this.submitPresentation.emit(null);
      this.removeExistingPresentation = false;
      return;
    }
    if (this.selectedFile) {
      this.submitPresentation.emit(this.selectedFile);
      this.removeExistingPresentation = false;
    }
  }

}
